import { Extension } from '@tiptap/core';
import { collaborationCursorPlugin } from './collaborationCursorPlugin';
import {
  ListenToRemoteCursorUpdates,
  SendCurrentCursorPosition,
} from './types';

/**
 * Options for the CollaborationCursorExtension.
 */
export interface CollaborationCursorOptions {
  /**
   * The unique ID of the client using the extension.
   */
  clientID: string;
  /**
   * The amount of time to wait before sending cursor updates, in milliseconds.
   * Defaults to 500ms.
   */
  debounceTime?: number;
  /**
   * A function to send the current cursor position to the server.
   */
  sendCurrentCursorPosition: SendCurrentCursorPosition | undefined;
  /**
   * A function to listen for remote cursor updates from the server.
   */
  listenToRemoteCursorUpdates: ListenToRemoteCursorUpdates | undefined;
  /**
   * A function to remove the cursor position of a client when they disconnect.
   */
  removeCursorPositionOnDestroy: ((clientID: string) => void) | undefined;
}

/**
 * An extension for ProseMirror that enables collaboration cursors.
 * @typeParam CollaborationCursorOptions - The options for the collaboration cursor.
 * @param {CollaborationCursorOptions} options - The options for the collaboration cursor.
 * @returns {Extension} - The ProseMirror extension.
 */
export const CollaborationCursorExtension =
  Extension.create<CollaborationCursorOptions>({
    name: 'firestoreCollabCursor',
    addOptions() {
      return {
        clientID: Math.floor(Math.random() * 0xffffffff).toString(),
        sendCurrentCursorPosition: undefined,
        listenToRemoteCursorUpdates: undefined,
        removeCursorPositionOnDestroy: undefined,
      };
    },
    addProseMirrorPlugins() {
      const {
        clientID,
        debounceTime,
        sendCurrentCursorPosition,
        listenToRemoteCursorUpdates,
        removeCursorPositionOnDestroy,
      } = this.options;

      if (
        !clientID ||
        !sendCurrentCursorPosition ||
        !listenToRemoteCursorUpdates ||
        !removeCursorPositionOnDestroy
      ) {
        throw new Error(
          'CollaborationCursorExtension requires all options to be set',
        );
      }

      return [
        collaborationCursorPlugin({
          clientID,
          debounceTime,
          sendCurrentCursorPosition,
          listenToRemoteCursorUpdates,
          removeCursorPositionOnDestroy,
        }),
      ];
    },
  });
